import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, finalize, map, Observable, of, share, switchMap, tap } from 'rxjs';
import { AuthService } from '../../core/services/auth/auth.service';
import { ApiConfigService } from '../../ssr-services/api-config/api-config.service';
import { SubscriberService } from '../../ssr-services/subscriber/subscriber.service';
import { distinctAuthenticatedState } from '../../utils/auth-distinct.pipe';
import { PaymentService, TransactionHistoryResponse } from '../payment/payment.service';

export interface RewardPointsResponse {
  rewardPoints: number;
  cashValue: number;
  referralCode: string;
  currency: string;
}

@Injectable({
  providedIn: 'root',
})
export class RewardPointsService {
  public userRewardPointsData = signal<RewardPointsResponse | null>(null);

  public transactionHistory = signal<TransactionHistoryResponse[]>([]);

  protected apiConfig = inject(ApiConfigService);

  protected http = inject(HttpClient);

  protected authService = inject(AuthService);

  protected paymentService = inject(PaymentService);

  protected subscriberService = inject(SubscriberService);

  protected isAuthenticated = toObservable(this.authService.isAuthenticated);

  public currency = computed(() => this.subscriberService.subscriberCurrency().currency);

  private rewardPointsRequest: Observable<RewardPointsResponse | null> | null = null;

  constructor() {
    effect(() => {
      const isAuthenticated = this.authService.isAuthenticated();

      if (!isAuthenticated) {
        this.userRewardPointsData.set(null);
      }
    });
  }

  public initAutoLoadUserRewardPointsData(): void {
    distinctAuthenticatedState(this.isAuthenticated)
      .pipe(switchMap(() => this.fetchUserRewardPointsData()))
      .subscribe();
  }

  public fetchUserRewardPointsData(): Observable<RewardPointsResponse | null> {
    if (this.rewardPointsRequest) {
      return this.rewardPointsRequest;
    }

    this.rewardPointsRequest = this.http
      .get<RewardPointsResponse>(this.apiConfig.url('subscriber/rewardPoints'))
      .pipe(
        map((result) => {
          this.userRewardPointsData.set(result);
          return result;
        }),
        catchError((err: unknown) => {
          console.warn(err);
          this.userRewardPointsData.set(null);
          return of(null);
        }),
        share(),
        finalize(() => {
          this.rewardPointsRequest = null;
        })
      );

    return this.rewardPointsRequest;
  }

  /**
   * Initializes the reward points component effects:
   * - Watches currency changes and loads transaction history
   * - Initializes auto-load of user reward points data
   */
  public initRewardPointsComponent(): void {
    effect(() => {
      if (this.currency()) {
        this.getTransactionHistory().subscribe();
      }
    });

    effect(() => {
      this.initAutoLoadUserRewardPointsData();
    });
  }

  /**
   * Fetches transaction history with error handling
   * Normalizes the response to always be an array and updates the transactionHistory signal
   */
  public getTransactionHistory(): Observable<TransactionHistoryResponse[]> {
    return this.paymentService.getTransactionHistory().pipe(
      catchError((err) => {
        console.error('Transaction history error:', err);
        return of([]);
      }),
      tap((res) => {
        const transactions = Array.isArray(res) ? res : [res];
        this.transactionHistory.set(transactions);
      }),
      map((res) => {
        return Array.isArray(res) ? res : [res];
      })
    );
  }
}
